import React from 'react';
import { Link } from 'react-router-dom';
import { FiStar } from 'react-icons/fi';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { getBetaProAccountInfo, getBetaProBannerMessage } from '../utils/subscriptionAccess';

function shortEndDate(info) {
  if (!info?.endsAt) return info?.endsLabel || null;
  try {
    return new Date(info.endsAt).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return info.endsLabel || null;
  }
}

function buildChipCopy(info, message) {
  if (!info) {
    return { label: 'Beta', title: message?.title || 'Programa beta Contsuite' };
  }
  const endShort = shortEndDate(info);
  const { daysRemaining, phase, graceEndsLabel } = info;

  if (phase === 'grace_after_beta') {
    return {
      label: 'Beta finalizada',
      title: graceEndsLabel
        ? `Finalizó la beta. Solo lectura hasta el ${graceEndsLabel}. Activá un plan en Planes.`
        : 'Finalizó la beta. Activá un plan en Planes.',
    };
  }
  if (phase === 'ended' || (daysRemaining != null && daysRemaining < 0)) {
    return {
      label: 'Activar plan',
      title: 'Tu beneficio beta venció. Activá la suscripción en Planes.',
    };
  }
  if (daysRemaining != null && daysRemaining >= 0) {
    const daysPart =
      daysRemaining === 0
        ? 'último día'
        : `${daysRemaining} día${daysRemaining === 1 ? '' : 's'}`;
    const label = endShort ? `Beta · ${daysPart} · ${endShort}` : `Beta · ${daysPart}`;
    return {
      label,
      title: message?.body
        ? `${message.title || 'Beta Contsuite'}. ${daysPart}${endShort ? `, vence ${endShort}` : ''}. ${message.body}`
        : `Plan Profesional incluido en la beta${endShort ? ` hasta ${endShort}` : ''}.`,
    };
  }
  return {
    label: 'Beta Contsuite',
    title: message?.title || 'Beneficio beta Contsuite',
  };
}

/** Chip compacto de beta (Inicio / Cuenta; no navbar). */
export default function BetaProPageBanner({ user, className = '' }) {
  const tc = useThemeClasses();
  const message = getBetaProBannerMessage(user);
  const info = getBetaProAccountInfo(user);
  if (!message && !info) return null;

  const tone = message?.tone || 'info';
  const isInfo = tone === 'info';
  const isGrace = tone === 'grace';
  const shellClass = isInfo
    ? tc.modoOscuro
      ? 'border-[#1F80FF]/40 bg-[#1F80FF]/15 text-blue-100 hover:bg-[#1F80FF]/25'
      : 'border-[#1F80FF]/35 bg-[#1F80FF]/10 text-[#004AAD] hover:bg-[#1F80FF]/15'
    : isGrace
      ? tc.modoOscuro
        ? 'border-amber-600/50 bg-amber-950/40 text-amber-100 hover:bg-amber-950/55'
        : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
      : tc.modoOscuro
        ? 'border-red-700/50 bg-red-950/40 text-red-100 hover:bg-red-950/55'
        : 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100';

  const ctaUrl = message?.ctaUrl || '/planes';
  const { label, title } = buildChipCopy(info, message);

  return (
    <Link
      to={ctaUrl}
      role="status"
      title={title}
      className={`inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold border transition-colors shrink-0 ${shellClass} ${className}`.trim()}
    >
      <FiStar className="w-3 h-3 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </Link>
  );
}
